// appointment-signalr.service.ts
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppointmentNotificationPayload } from './notifications.types';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private hubConnection!: HubConnection;
  private baseHubUrl: string = '';
  private currentDoctorId?: number;
  private currentHospitalId?: number | null;

  // Observable stream to notify components
  private appointmentBookedSource = new BehaviorSubject<AppointmentNotificationPayload | null>(null);
  appointmentBooked$ = this.appointmentBookedSource.asObservable();

  constructor(private toastr: ToastrService) {
    this.setupSignalRConnection();
  }

  /**
   * Setup SignalR connection
   */
  private setupSignalRConnection(): void {
    console.log('Setting up SignalR connection...');

    try {
      this.baseHubUrl = environment.baseURl.substring(0, environment.baseURl.lastIndexOf('/api')) + '/notificationHub';
      this.hubConnection = new HubConnectionBuilder()
        .configureLogging(LogLevel.Information)
        .withUrl(this.baseHubUrl, {
          accessTokenFactory: () => {
            return localStorage.getItem('accessToken') || ''
          }
        })
        .withAutomaticReconnect()
        .build();

      this.hubConnection
        .start()
        .then(() => {
          console.log('SignalR connected successfully');
          if (this.currentDoctorId && this.currentHospitalId) {
            this.joinDoctorCalendarGroup(this.currentDoctorId, this.currentHospitalId);
          }
        })
        .catch(err => {
          console.error('Error starting SignalR connection:', err);
        });

      this.hubConnection.onreconnected(() => {
        console.log('Hub reconnected');
        if (this.currentDoctorId && this.currentHospitalId) {
          this.joinDoctorCalendarGroup(this.currentDoctorId, this.currentHospitalId);
        }
      });

      this.hubConnection.on('AppointmentNotification', (response: AppointmentNotificationPayload) => {
        if (!response) return;
        this.appointmentBookedSource.next(response);
      });

    } catch (error) {
      console.error('Error setting up SignalR:', error);
    }
  }

  joinDoctorCalendarGroup(doctorId: number, hospitalId?: number | null): void {
    if (this.hubConnection && this.hubConnection.state === "Connected") {
      this.hubConnection.invoke("JoinDoctorCalendarGroup", hospitalId, doctorId)
        .catch(err => console.error("Error joining doctor calendar group:", err));
    }
    this.currentDoctorId = doctorId;
    this.currentHospitalId = hospitalId;
  }

  leaveDoctorCalendarGroup(doctorId: number, hospitalId: number): void {
    if (this.hubConnection && this.hubConnection.state === "Connected") {
      this.hubConnection.invoke("LeaveDoctorCalendarGroup", hospitalId, doctorId)
        .catch(err => console.error("Error leaving doctor calendar group:", err));
    }
    if (this.currentDoctorId === doctorId && this.currentHospitalId === hospitalId) {
      this.currentDoctorId = undefined;
      this.currentHospitalId = undefined;
    }
  }
}
